import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const guideArticles = [
  { slug: 'guia-cssbuy-completo', name: 'Guia CSSBuy Completo' },
  { slug: 'como-usar-cssbuy', name: 'Como Usar CSSBuy' },
  { slug: 'melhores-spreadsheets-cssbuy', name: 'Melhores Spreadsheets' },
  { slug: 'comparacao-agentes-shopping', name: 'Comparação de Agentes' },
  { slug: 'seguranca-cssbuy', name: 'Segurança CSSBuy' },
  { slug: 'faq-cssbuy', name: 'FAQ CSSBuy' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setIsCategoryOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setIsGuideOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeAll = () => {
    setIsCategoryOpen(false);
    setIsGuideOpen(false);
    setIsMobileOpen(false);
  };

  const navBgClass = isScrolled
    ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
    : 'bg-black/25 backdrop-blur-sm';

  const brandClass = isScrolled
    ? 'text-foreground-900'
    : 'text-white';

  const linkClass = isScrolled
    ? 'text-foreground-700 hover:text-primary-500'
    : 'text-white/90 hover:text-white';

  const btnBorderClass = isScrolled
    ? 'border-foreground-300 text-foreground-800 hover:bg-background-100'
    : 'border-white/50 text-white hover:bg-white/15';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass}`}>
      <div className="flex items-center justify-between h-[68px] px-4 md:px-8">
        {/* Brand */}
        <Link
          to="/"
          onClick={closeAll}
          className={`font-heading text-xl md:text-2xl font-bold lowercase tracking-tight transition-colors duration-200 ${brandClass} hover:opacity-80`}
        >
          cssbuy<span className={isScrolled ? 'text-primary-500' : 'text-primary-300'}>.</span>today
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-3">
          <Link to="/" onClick={closeAll} className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${linkClass}`}>
            Início
          </Link>

          {/* Guia de Compras dropdown */}
          <div ref={guideRef} className="relative">
            <button
              onClick={() => { setIsGuideOpen(!isGuideOpen); setIsCategoryOpen(false); }}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${btnBorderClass}`}
            >
              Guia de Compras
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isGuideOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {isGuideOpen && (
              <div className="absolute right-0 mt-2 w-[260px] bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 animate-[fadeIn_0.15s_ease-out]">
                {guideArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/${article.slug}`}
                    onClick={closeAll}
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors duration-150 whitespace-nowrap"
                  >
                    {article.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Categorias dropdown */}
          <div ref={categoryRef} className="relative">
            <button
              onClick={() => { setIsCategoryOpen(!isCategoryOpen); setIsGuideOpen(false); }}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${btnBorderClass}`}
            >
              {t('nav.categories')}
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isCategoryOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {isCategoryOpen && (
              <div className="absolute right-0 mt-2 w-[580px] bg-background-50 rounded-xl shadow-lg border border-background-200/70 p-4 animate-[fadeIn_0.15s_ease-out]">
                <div className="grid grid-cols-4 gap-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={closeAll}
                      className="px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-all duration-150 whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => { setIsMobileOpen(!isMobileOpen); setIsCategoryOpen(false); setIsGuideOpen(false); }}
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full transition-colors duration-200 cursor-pointer ${isScrolled ? 'text-foreground-800 hover:bg-background-100' : 'text-white hover:bg-white/10'}`}
          aria-label="Menu"
        >
          <i className={`text-xl ${isMobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {/* Mobile menu */}
      {isMobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 animate-[fadeIn_0.2s_ease-out] max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3">
            <Link to="/" onClick={closeAll} className="block py-3 text-sm font-medium text-foreground-800 border-b border-background-200">
              Início
            </Link>

            <div className="py-3 border-b border-background-200">
              <p className="text-xs font-semibold text-foreground-500 uppercase mb-2">Guia de Compras</p>
              {guideArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/${article.slug}`}
                  onClick={closeAll}
                  className="block py-2 text-sm text-foreground-700 hover:text-primary-500 transition-colors duration-150"
                >
                  {article.name}
                </Link>
              ))}
            </div>

            <div className="py-3">
              <p className="text-xs font-semibold text-foreground-500 uppercase mb-2">{t('nav.categories')}</p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeAll}
                    className="py-2 text-sm text-foreground-700 hover:text-primary-500 transition-colors duration-150"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}